import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, DatePicker, Select, Button, Card, Row, Col, Typography, message, Divider, Modal, Space, Radio, Tag, Statistic, Grid } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, ShoppingCartOutlined, DashboardOutlined, PercentageOutlined, WalletOutlined, PlusOutlined } from '@ant-design/icons';
import client from '../api/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;

const PurchaseForm = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user, hasPermission } = useAuth();
    const canPost = user?.role === 'ADMIN' || user?.role === 'MANAGER' || hasPermission('post_transaction');
    const canCreate = user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'STAFF' || hasPermission('create_purchase');
    const [submitMode, setSubmitMode] = useState('draft');
    const [searchParams] = useSearchParams();
    const commodity = searchParams.get('commodity') || 'palay';
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [partners, setPartners] = useState([]);
    const [branches, setBranches] = useState([]);
    const [items, setItems] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPartnerModalVisible, setIsPartnerModalVisible] = useState(false);
    const [partnerForm] = Form.useForm();

    useEffect(() => {
        fetchData();
        fetchNextTicket();
    }, []);

    const fetchData = async () => {
        try {
            const [pRes, bRes, cRes, iRes] = await Promise.all([
                client.get('/master/partners'),
                client.get('/master/branches'),
                client.get('/transactions/crop-cycles'),
                client.get('/master/items')
            ]);
            setPartners(pRes.data.filter(p => p.type === 'FARMER' || p.type === 'SUPPLIER' || p.type === 'BOTH'));
            setBranches(bRes.data);
            setCycles(cRes.data);
            const categoryName = commodity.charAt(0).toUpperCase() + commodity.slice(1);
            setItems(iRes.data.filter(i => i.category?.name === categoryName));
        } catch (error) {
            message.error('Failed to load form data');
            console.error('Error fetching form data:', error);
        }
    };

    const fetchNextTicket = async () => {
        try {
            const { data } = await client.get('/transactions/purchases/next-ticket');
            form.setFieldsValue({ ticket_number: data.ticket_number });
        } catch (error) {
            console.error('Failed to fetch ticket number');
        }
    };

    const getNextSupplierCode = (items) => {
        const prefix = 'SUP-';
        const maxNumber = items.reduce((max, item) => {
            const match = typeof item.code === 'string' ? item.code.match(/^SUP-(\d+)$/i) : null;
            if (!match) return max;
            const value = parseInt(match[1], 10);
            return Number.isNaN(value) ? max : Math.max(max, value);
        }, 0);
        const nextNumber = (maxNumber + 1).toString().padStart(3, '0');
        return `${prefix}${nextNumber}`;
    };

    const openPartnerModal = () => {
        const nextCode = getNextSupplierCode(partners);
        partnerForm.setFieldsValue({ code: nextCode, type: 'FARMER' });
        setIsPartnerModalVisible(true);
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                transaction_date: values.transaction_date.format('YYYY-MM-DD'),
            };
            const { data } = await client.post('/transactions/purchases', payload);
            if (submitMode === 'post') {
                await client.post(`/transactions/purchases/${data.id}/post`);
                message.success('Purchase approved and posted to ledger');
            } else {
                message.success('Purchase saved as draft');
            }
            navigate('/purchasing');
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to save');
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePartner = async (values) => {
        try {
            const { data } = await client.post('/master/partners', values);
            message.success('Supplier added');
            await fetchData();
            form.setFieldsValue({ partner_id: data.id });
            setIsPartnerModalVisible(false);
            partnerForm.resetFields();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to add supplier';
            message.error(errorMessage);
        }
    };

    const gross = Form.useWatch('gross_weight', form) || 0;
    const tare = Form.useWatch('tare_weight', form) || 0;
    const price = Form.useWatch('base_price', form) || 0;
    const moisture = Form.useWatch('moisture_content', form) || 14;
    const weighingMode = Form.useWatch('weighing_mode', form) || 'simple';
    const qualityMode = Form.useWatch('quality_mode', form) || 'none';

    const pricePerKg = price;
    const baseNetWeight = weighingMode === 'gross_tare' ? (gross - tare) : gross;
    const moistureDeduction = qualityMode === 'adjustment' && moisture > 14
        ? baseNetWeight * (moisture - 14) * 0.005
        : 0;
    const netWeight = Math.max(0, baseNetWeight - moistureDeduction);
    const moistureAdj = qualityMode === 'adjustment' ? (moisture > 14 ? (moisture - 14) * -0.50 : 0) : 0;
    const netPrice = pricePerKg + moistureAdj;
    const totalAmount = netWeight * netPrice;

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', marginBottom: 32 }}>
                <div>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/purchasing')}
                        style={{ marginBottom: 12, borderRadius: 8 }}
                    >
                        Back to Purchases
                    </Button>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>New {commodity.charAt(0).toUpperCase() + commodity.slice(1)} Purchase</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Generate raw material procurement receipts for inventory.</Typography.Text>
                </div>
                <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                    <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Ticket Reference</Typography.Text>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#3b82f6' }}>
                        #{form.getFieldValue('ticket_number') || '...'}
                    </div>
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                    transaction_date: dayjs(),
                    moisture_content: 14,
                    weighing_mode: 'simple',
                    quality_mode: 'none',
                }}
            >
                <Form.Item
                    name="ticket_number"
                    hidden
                    rules={[{ required: true, message: 'Ticket number is required' }]}
                >
                    <Input />
                </Form.Item>
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={16}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <Card className="premium-card" title={<Space><ShoppingCartOutlined />Identification & Source</Space>}>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="branch_id" label={<Typography.Text strong>Receiving Branch</Typography.Text>} rules={[{ required: true }]}>
                                            <Select placeholder="Select Location" style={{ height: 40 }}>
                                                {branches.map(b => <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="transaction_date" label={<Typography.Text strong>Received Date</Typography.Text>} rules={[{ required: true }]}>
                                            <DatePicker style={{ width: '100%', height: 40, borderRadius: 8 }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="storage_code" label={<Typography.Text strong>Storage Code</Typography.Text>} rules={[{ required: true }]}>
                                            <Input style={{ height: 40, borderRadius: 8 }} placeholder="Enter storage code for this batch" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item name="item_id" label={<Typography.Text strong>{commodity.charAt(0).toUpperCase() + commodity.slice(1)} Variety</Typography.Text>} rules={[{ required: true }]}>
                                            <Select placeholder="Select Variety" style={{ height: 40 }} showSearch optionFilterProp="children">
                                                {items.map(i => <Select.Option key={i.id} value={i.id}>{i.name} ({i.code})</Select.Option>)}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item name="partner_id" label={<Typography.Text strong>Farmer / Supplier</Typography.Text>} rules={[{ required: true }]}>
                                            <Select
                                                style={{ height: 40 }}
                                                showSearch
                                                optionFilterProp="children"
                                                placeholder="Search farmer name or code"
                                                dropdownRender={(menu) => (
                                                    <>
                                                        {menu}
                                                        <Divider style={{ margin: '8px 0' }} />
                                                        <div style={{ padding: '0 8px 8px' }}>
                                                            <Button type="primary" block icon={<PlusOutlined />} onClick={openPartnerModal} style={{ borderRadius: 8 }}>
                                                                Add New Supplier
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            >
                                                {partners.map(p => (
                                                    <Select.Option key={p.id} value={p.id}>{p.name} ({p.code})</Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prevValues, currentValues) => prevValues.partner_id !== currentValues.partner_id}
                                        >
                                            {({ getFieldValue }) => {
                                                const selectedPartnerId = getFieldValue('partner_id');
                                                const partnerCycles = cycles.filter(c => c.farm.partner_id === selectedPartnerId && c.status !== 'COMPLETED' && c.status !== 'FAILED');

                                                if (!selectedPartnerId || partnerCycles.length === 0) return null;

                                                return (
                                                    <Form.Item name="crop_cycle_id" label={<Typography.Text strong>Link to Active Farm Cycle</Typography.Text>} style={{ marginTop: 8 }}>
                                                        <Select allowClear placeholder="Associate with ongoing cycle" style={{ height: 40 }}>
                                                            {partnerCycles.map(c => (
                                                                <Select.Option key={c.id} value={c.id}>
                                                                    {c.control_number} - {c.crop_type} ({c.farm.name})
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item name="remarks" label={<Typography.Text strong>Remarks</Typography.Text>}>
                                            <Input.TextArea rows={2} placeholder="Optional notes for this purchase" style={{ borderRadius: 8 }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card
                                className="premium-card"
                                title={<Space><DashboardOutlined />Weighing Log</Space>}
                                extra={
                                    commodity === 'palay' ? (
                                        <Form.Item name="weighing_mode" noStyle>
                                            <Radio.Group size="small" buttonStyle="solid">
                                                <Radio.Button value="simple">Direct</Radio.Button>
                                                <Radio.Button value="gross_tare">Truck Scale</Radio.Button>
                                            </Radio.Group>
                                        </Form.Item>
                                    ) : null
                                }
                            >
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} md={weighingMode === 'gross_tare' ? 12 : 24}>
                                        <Form.Item
                                            name="gross_weight"
                                            label={<Typography.Text strong>{weighingMode === 'gross_tare' ? "Full Truck (Gross)" : "Net Weight (Input)"}</Typography.Text>}
                                            rules={[{ required: true }]}
                                        >
                                            <InputNumber
                                                style={{ width: '100%', height: 40, borderRadius: 8 }}
                                                precision={2}
                                                placeholder="0.00"
                                            />
                                        </Form.Item>
                                    </Col>
                                    {weighingMode === 'gross_tare' && (
                                        <Col xs={24} md={12}>
                                            <Form.Item name="tare_weight" label={<Typography.Text strong>Empty Truck (Tare)</Typography.Text>} rules={[{ required: true }]}>
                                                <InputNumber
                                                    style={{ width: '100%', height: 40, borderRadius: 8 }}
                                                    precision={2}
                                                    placeholder="0.00"
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}
                                </Row>
                                {weighingMode === 'gross_tare' && (
                                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: 12, marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography.Text type="secondary">Calculated Net Content:</Typography.Text>
                                        <Typography.Text strong style={{ color: '#3b82f6', fontSize: 18 }}>{netWeight.toLocaleString()} kg</Typography.Text>
                                    </div>
                                )}
                            </Card>

                            <Card
                            className="premium-card"
                            title={<Space><PercentageOutlined />Quality & Valuation</Space>}
                            extra={
                                commodity === 'palay' ? (
                                    <Form.Item name="quality_mode" noStyle>
                                        <Radio.Group size="small" buttonStyle="solid">
                                                <Radio.Button value="none">Flat Rate</Radio.Button>
                                                <Radio.Button value="adjustment">MC Basis</Radio.Button>
                                            </Radio.Group>
                                        </Form.Item>
                                    ) : null
                                }
                            >
                                <Row gutter={16}>
                                    <Col span={qualityMode === 'adjustment' ? 8 : 24}>
                                        <Form.Item name="base_price" label={<Typography.Text strong>Base Rate (PHP / kg)</Typography.Text>} rules={[{ required: true }]}>
                                            <InputNumber
                                                style={{ width: '100%', height: 40, borderRadius: 8 }}
                                                precision={2}
                                                placeholder="0.00"
                                            />
                                        </Form.Item>
                                    </Col>
                                    {qualityMode === 'adjustment' && (
                                        <Col span={commodity === 'palay' ? 8 : 12}>
                                            <Form.Item name="moisture_content" label={<Typography.Text strong>Moisture %</Typography.Text>} rules={[{ required: true }]}>
                                                <InputNumber
                                                    style={{ width: '100%', height: 40, borderRadius: 8 }}
                                                    precision={2}
                                                    placeholder="14.00"
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}
                                </Row>
                            </Card>
                        </Space>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card
                            className="premium-card"
                            title={<Space><WalletOutlined />Settlement Summary</Space>}
                            style={{ position: 'sticky', top: 24 }}
                        >
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <div style={{ textAlign: 'center', paddingBottom: 12 }}>
                                    <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Final Weight</Typography.Text>
                                    <div style={{ fontSize: 32, fontWeight: 600, color: 'var(--text-primary)' }}>
                                        {netWeight.toLocaleString()} <Typography.Text style={{ fontSize: 16 }}>kg</Typography.Text>
                                    </div>
                                </div>

                                <Divider style={{ margin: '0' }} />

                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
                                    <Typography.Text type="secondary">Base Rate</Typography.Text>
                                    <Typography.Text strong>₱{pricePerKg.toFixed(2)} / kg</Typography.Text>
                                </div>
                                {qualityMode === 'adjustment' && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 12 }}>
                                        <Typography.Text type="secondary">Moisture Shrink</Typography.Text>
                                        <Typography.Text type={moistureDeduction > 0 ? "danger" : "secondary"} strong>
                                            {moistureDeduction > 0 ? '-' : ''} {moistureDeduction.toFixed(2)} kg
                                        </Typography.Text>
                                    </div>
                                )}

                                {qualityMode === 'adjustment' && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 12 }}>
                                        <Typography.Text type="secondary">MC Adjustment</Typography.Text>
                                        <Typography.Text type={moistureAdj < 0 ? "danger" : "success"} strong>
                                            {moistureAdj < 0 ? '-' : '+'} ₱{Math.abs(moistureAdj).toFixed(2)}
                                        </Typography.Text>
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: '#f8fafc', borderRadius: 10 }}>
                                    <Typography.Text strong>Final Price / kg</Typography.Text>
                                    <Typography.Text strong style={{ color: '#10b981' }}>₱{netPrice.toFixed(2)}</Typography.Text>
                                </div>

                                <div style={{
                                    marginTop: 16,
                                    padding: '24px',
                                    background: '#ecfdf5',
                                    border: '1px solid #d1fae5',
                                    borderRadius: 16,
                                    textAlign: 'center'
                                }}>
                                    <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Payable Amount</Typography.Text>
                                    <Title level={2} style={{ margin: 0, color: '#059669', fontWeight: 600 }}>
                                        ₱{totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Title>
                                </div>

                                {canPost && (
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        icon={<SaveOutlined />}
                                        block
                                        loading={loading && submitMode === 'post'}
                                        onClick={() => setSubmitMode('post')}
                                        style={{ height: 55, borderRadius: 12, marginTop: 20, fontSize: 18, fontWeight: 700 }}
                                    >
                                        Approve & Post
                                    </Button>
                                )}

                                {canCreate && (
                                    <Button
                                        htmlType="submit"
                                        size="large"
                                        icon={<SaveOutlined />}
                                        block
                                        loading={loading && submitMode === 'draft'}
                                        onClick={() => setSubmitMode('draft')}
                                        style={{ height: 48, borderRadius: 12, marginTop: canPost ? 8 : 20, fontSize: 16 }}
                                    >
                                        Save as Draft
                                    </Button>
                                )}

                                <Typography.Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block', color: 'var(--text-secondary)' }}>
                                    {canPost ? 'Approve & Post commits to ledger immediately.' : 'Draft records can be posted later by a Manager or Admin.'}
                                </Typography.Text>
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Form>

            <Modal
                title="Register New Supplier"
                open={isPartnerModalVisible}
                onCancel={() => setIsPartnerModalVisible(false)}
                footer={null}
                centered
                bodyStyle={{ padding: isMobile ? 12 : 24 }}
                width={isMobile ? '100%' : 520}
                style={{ top: isMobile ? 12 : 24 }}
            >
                <Form form={partnerForm} layout="vertical" onFinish={handleCreatePartner} initialValues={{ type: 'FARMER' }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="code" label={<Typography.Text strong>Supplier Code</Typography.Text>} rules={[{ required: true }]}>
                                <Input readOnly style={{ height: 40, borderRadius: 8, background: '#f8fafc' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="type" label={<Typography.Text strong>Category</Typography.Text>} rules={[{ required: true }]}>
                                <Select style={{ height: 40 }}>
                                    <Select.Option value="FARMER">Farmer</Select.Option>
                                    <Select.Option value="SUPPLIER">Supplier</Select.Option>
                                    <Select.Option value="BOTH">Both</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="name" label={<Typography.Text strong>Full Name / Business Name</Typography.Text>} rules={[{ required: true }]}>
                        <Input placeholder="John Doe" style={{ height: 40, borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item name="phone" label={<Typography.Text strong>Contact Number</Typography.Text>}>
                        <Input placeholder="09XX XXX XXXX" style={{ height: 40, borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item name="address" label={<Typography.Text strong>Physical Address</Typography.Text>}>
                        <Input.TextArea placeholder="Complete address details" rows={3} style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                        <Button onClick={() => setIsPartnerModalVisible(false)} style={{ borderRadius: 8, height: 40, padding: '0 24px' }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ borderRadius: 8, height: 40, padding: '0 32px' }}>Save Supplier</Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default PurchaseForm;
